import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import useAuthStore from '../store/auth';

const CURRENCIES = [
  { code:'EUR', symbol:'€', label:'Euro (€)' },
  { code:'USD', symbol:'$', label:'Dollar ($)' },
  { code:'MGA', symbol:'Ar', label:'Ariary (Ar) - Madagascar' },
  { code:'XOF', symbol:'CFA', label:'Franc CFA (XOF)' },
  { code:'GBP', symbol:'£', label:'Livre sterling (£)' },
  { code:'ZAR', symbol:'R', label:'Rand (R) - Afrique du Sud' },
  { code:'CHF', symbol:'CHF', label:'Franc suisse (CHF)' },
  { code:'CAD', symbol:'CA$', label:'Dollar canadien (CA$)' },
];

export default function RegisterPage() {
  const { t } = useTranslation();
  const { register } = useAuthStore();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name:'', email:'', password:'',
    account_type:'personal', language:'fr', currency:'EUR',
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.password.length < 6) return toast.error(t('password_min'));
    setLoading(true);
    try {
      await register(form);
      // Accepter invitation en attente apres inscription
      const pendingInvite = localStorage.getItem('bf_pending_invite');
      if (pendingInvite) {
        try {
          const { familyAPI } = await import('../api');
          await familyAPI.acceptInvite(pendingInvite);
          localStorage.removeItem('bf_pending_invite');
          toast.success('Compte cree et groupe familial rejoint !');
          window.location.href = '/family';
          return;
        } catch {}
      }
      setDone(true);
      toast.success(t('account_created'));
    } catch (err) {
      toast.error(err.response?.data?.error || t('error'));
    } finally { setLoading(false); }
  };

  const inp = {
    width:'100%', background:'var(--surface2)', border:'1px solid var(--border)',
    borderRadius:10, padding:'12px 16px', color:'var(--text)', fontSize:15, outline:'none',
    boxSizing:'border-box',
  };

  // Ecran "verifiez votre email"
  if (done) return (
    <div style={{ minHeight:'100vh', display:'flex', alignItems:'center', justifyContent:'center', background:'radial-gradient(ellipse 80% 60% at 50% -10%,#2d1f7a44,transparent),var(--bg)', padding:20 }}>
      <div style={{ background:'var(--surface)', border:'1px solid var(--border)', borderRadius:24, padding:48, width:'100%', maxWidth:440, textAlign:'center' }}>
        <div style={{ fontSize:64, marginBottom:16 }}>📧</div>
        <h2 style={{ fontFamily:'Syne,sans-serif', fontWeight:800, fontSize:24, marginBottom:12 }}>{t('verify_email_title')}</h2>
        <p style={{ color:'var(--text2)', fontSize:15, lineHeight:1.7, marginBottom:32 }}>
          {t('verify_email_msg')}
          <br /><br />
          <strong style={{ color:'var(--accent)' }}>{form.email}</strong>
        </p>
        <div style={{ background:'var(--surface2)', border:'1px solid var(--border)', borderRadius:12, padding:16, marginBottom:24, fontSize:13, color:'var(--text2)' }}>
          Verifie aussi tes spams si tu ne trouves pas l&apos;email.
        </div>
        <button onClick={() => navigate('/login')} style={{ width:'100%', padding:14, background:'var(--accent)', color:'#fff', border:'none', borderRadius:10, fontFamily:'Syne,sans-serif', fontWeight:700, fontSize:15 }}>
          Aller a la connexion
        </button>
      </div>
    </div>
  );

  return (
    <div style={{ minHeight:'100vh', display:'flex', alignItems:'center', justifyContent:'center', background:'radial-gradient(ellipse 80% 60% at 50% -10%,#2d1f7a44,transparent),var(--bg)', padding:20 }}>
      <div style={{ background:'var(--surface)', border:'1px solid var(--border)', borderRadius:24, padding:48, width:'100%', maxWidth:460, boxShadow:'0 40px 80px #00000060' }}>
        <div style={{ fontFamily:'Syne,sans-serif', fontSize:28, fontWeight:800, background:'linear-gradient(135deg,#7c6af7,#f7c26a)', WebkitBackgroundClip:'text', WebkitTextFillColor:'transparent', marginBottom:4 }}>
          BudgetFlow
        </div>
        <h2 style={{ fontFamily:'Syne,sans-serif', fontSize:20, fontWeight:700, marginBottom:4 }}>{t('register_title')}</h2>
        <p style={{ color:'var(--text2)', fontSize:13, marginBottom:28 }}>
          {t('register_sub')} <span style={{ color:'var(--accent3)', fontWeight:600 }}>⭐</span>
        </p>

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom:16 }}>
            <label style={{ display:'block', fontSize:13, color:'var(--text2)', marginBottom:8 }}>{t('full_name')}</label>
            <input type="text" required value={form.name} onChange={e => set('name', e.target.value)} style={inp} placeholder="Jean Dupont" />
          </div>
          <div style={{ marginBottom:16 }}>
            <label style={{ display:'block', fontSize:13, color:'var(--text2)', marginBottom:8 }}>{t('email')}</label>
            <input type="email" required value={form.email} onChange={e => set('email', e.target.value)} style={inp} placeholder="jean@email.com" />
          </div>
          <div style={{ marginBottom:16 }}>
            <label style={{ display:'block', fontSize:13, color:'var(--text2)', marginBottom:8 }}>{t('password')} (min. 6)</label>
            <input type="password" required value={form.password} onChange={e => set('password', e.target.value)} style={inp} placeholder="••••••••" />
          </div>

          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:16 }}>
            <div>
              <label style={{ display:'block', fontSize:13, color:'var(--text2)', marginBottom:8 }}>{t('account_type')}</label>
              <select value={form.account_type} onChange={e => set('account_type', e.target.value)} style={{ ...inp, padding:'11px 12px' }}>
                <option value="personal">{t('personal')}</option>
                <option value="family">{t('family_account')}</option>
              </select>
            </div>
            <div>
              <label style={{ display:'block', fontSize:13, color:'var(--text2)', marginBottom:8 }}>{t('language')}</label>
              <select value={form.language} onChange={e => set('language', e.target.value)} style={{ ...inp, padding:'11px 12px' }}>
                <option value="fr">Francais</option>
                <option value="en">English</option>
              </select>
            </div>
          </div>

          <div style={{ marginBottom:24 }}>
            <label style={{ display:'block', fontSize:13, color:'var(--text2)', marginBottom:8 }}>{t('currency')}</label>
            <select value={form.currency} onChange={e => set('currency', e.target.value)} style={{ ...inp, padding:'11px 12px' }}>
              {CURRENCIES.map(c => (
                <option key={c.code} value={c.code}>{c.label}</option>
              ))}
            </select>
          </div>

          <button type="submit" disabled={loading} style={{ width:'100%', padding:14, background:'var(--accent)', color:'#fff', border:'none', borderRadius:10, fontFamily:'Syne,sans-serif', fontWeight:700, fontSize:15, opacity:loading?0.7:1 }}>
            {loading ? t('loading') : t('register_title')}
          </button>
        </form>

        <p style={{ textAlign:'center', marginTop:20, fontSize:13, color:'var(--text3)' }}>
          {t('already_account')} <Link to="/login" style={{ color:'var(--accent)', textDecoration:'none' }}>{t('login')}</Link>
        </p>
      </div>
    </div>
  );
}